import axios from '../axios';
import {
    TWILIO_TOKEN_PATH,
    TWILIO_CALL_LOGS_PATH,
    TWILIO_RECORDINGS_PATH,
    getTwilioRecordingPath,
    deleteTwilioRecordingPath,
    getTwilioRecordingTranscriptionPath,
} from '../ApiUrl';

/**
 * POST /twilio/token
 * @param {string} identity
 * @returns {{ success, token, identity, expires_in } | { hasError, message }}
 */
export const fetchVoiceTokenAPI = async (identity) => {
    try {
        const response = await axios.post(TWILIO_TOKEN_PATH, { identity });
        return response;
    } catch (error) {
        console.log('fetchVoiceTokenAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to fetch Twilio token.' };
    }
};

/**
 * GET /twilio/call-logs
 * @param {{ page?, limit?, status?, direction?, identity?, from_date?, to_date? }} params
 */
export const getCallLogsAPI = async (params = {}) => {
    try {
        const response = await axios.get(TWILIO_CALL_LOGS_PATH, { params });
        return response;
    } catch (error) {
        console.log('getCallLogsAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to fetch call logs.' };
    }
};

/**
 * GET /twilio/recordings
 * @param {{ page?, limit?, call_sid?, status?, identity?, from_date?, to_date? }} params
 */
export const getRecordingsAPI = async (params = {}) => {
    try {
        const response = await axios.get(TWILIO_RECORDINGS_PATH, { params });
        return response;
    } catch (error) {
        console.log('getRecordingsAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to fetch recordings.' };
    }
};

/**
 * GET /twilio/recordings/:recordingSid
 * @param {string} recordingSid
 */
export const getRecordingByIdAPI = async (recordingSid) => {
    try {
        const response = await axios.get(getTwilioRecordingPath(recordingSid));
        return response;
    } catch (error) {
        console.log('getRecordingByIdAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to fetch recording.' };
    }
};

/**
 * DELETE /twilio/recordings/:recordingSid
 * @param {string} recordingSid
 */
export const deleteRecordingAPI = async (recordingSid) => {
    try {
        const response = await axios.delete(deleteTwilioRecordingPath(recordingSid));
        return response;
    } catch (error) {
        console.log('deleteRecordingAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to delete recording.' };
    }
};

/**
 * GET /twilio/recordings/:recordingSid/transcription
 * @param {string} recordingSid
 * @returns {{ hasError: false, data } | { hasError: false, notReady: true, data: null } | { hasError: true, message }}
 */
export const getTranscriptionAPI = async (recordingSid) => {
    try {
        const response = await axios.get(getTwilioRecordingTranscriptionPath(recordingSid));
        if (response?.hasError) {
            if (response.status === 404) {
                return { hasError: false, notReady: true, data: null };
            }
            return response;
        }
        const payload = response?.data;
        // Support both { data: { ... } } and flat { transcription_status, ... }
        const data = payload?.data ?? (payload?.transcription_status ? payload : null);
        return { hasError: false, data };
    } catch (error) {
        console.log('getTranscriptionAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to load transcription.' };
    }
};

/**
 * POST /twilio/recordings/:recordingSid/transcription — refresh from Twilio
 * @param {string} recordingSid
 */
export const syncTranscriptionAPI = async (recordingSid) => {
    try {
        const response = await axios.post(getTwilioRecordingTranscriptionPath(recordingSid));
        if (response?.hasError) {
            if (response.status === 404) {
                return { hasError: false, notReady: true, data: null };
            }
            return response;
        }
        const payload = response?.data;
        // Support both { data: { ... } } and flat { transcription_status, ... }
        const data = payload?.data ?? (payload?.transcription_status ? payload : null);
        return { hasError: false, data };
    } catch (error) {
        console.log('syncTranscriptionAPI catch', error);
        return { hasError: true, message: error?.message || 'Failed to sync transcription.' };
    }
};
